import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ProcessorDetails } from '@/lib/api/processors'

interface MerchantScaleConfigurationCardProps {
    processor: ProcessorDetails
}

const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const getAccountId = (options: Record<string, unknown> | null) => {
    return options?.accountId as string | undefined
}

const getConnectionState = (options: Record<string, unknown> | null) => {
    return options?.connectionState as 'pending' | 'connected' | 'disconnected' | undefined
}

const getRadarEnabled = (options: Record<string, unknown> | null) => {
    const radar = options?.radar as { enabled?: boolean } | undefined
    return radar?.enabled ?? false
}

export const MerchantScaleConfigurationCard = ({ processor }: MerchantScaleConfigurationCardProps) => {
    const { t } = useTranslation('processors')
    const accountId = getAccountId(processor.options)
    const connectionState = getConnectionState(processor.options)
    const radarEnabled = getRadarEnabled(processor.options)

    const getConnectionBadge = () => {
        switch (connectionState) {
            case 'connected':
                return <Badge variant="default">{t('connected')}</Badge>
            case 'pending':
                return <Badge variant="secondary">{t('pending')}</Badge>
            case 'disconnected':
                return <Badge variant="destructive">{t('disconnected')}</Badge>
            default:
                return <Badge variant="outline">{t('notConfigured')}</Badge>
        }
    }

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('merchantScaleConfiguration')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-sm text-muted-foreground">{t('accountId')}</p>
                        <p className="font-mono text-sm">{accountId || t('notConfigured')}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('connectionStatus')}</p>
                        {getConnectionBadge()}
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('radarStatus')}</p>
                        {radarEnabled ? (
                            <Badge variant="default">{t('enabled')}</Badge>
                        ) : (
                            <Badge variant="outline">{t('disabled')}</Badge>
                        )}
                    </div>

                    {processor.baseCurrency && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('baseCurrency')}</p>
                            <p className="font-semibold">{processor.baseCurrency}</p>
                        </div>
                    )}

                    <div>
                        <p className="text-sm text-muted-foreground">{t('apiVersion')}</p>
                        <p className="font-semibold">v1.0</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
                        <p className="font-semibold">{formatDate(processor.createdAt)}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
                        <p className="font-semibold">{formatDate(processor.updatedAt)}</p>
                    </div>

                    {processor.supportedCurrencies && processor.supportedCurrencies.length > 0 && (
                        <div className="col-span-full">
                            <p className="text-sm text-muted-foreground mb-2">{t('supportedCurrencies')}</p>
                            <div className="flex flex-wrap gap-2">
                                {processor.supportedCurrencies.map((currency) => (
                                    <Badge key={currency} variant="outline">
                                        {currency}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-6 rounded-lg bg-blue-50 p-4 border border-blue-200">
                    <p className="text-sm text-blue-700">
                        <strong>{t('note')}:</strong> {t('merchantScaleNote')}
                    </p>
                </div>
            </CardContent>
        </Card>
    )
}
